import { useRef, useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { FileResponseObject } from '@components/file-field/file-input';
import { Form } from '@components/form';
import { IconCollapse } from '@components/icons/collapse';
import { IconDynamicField } from '@components/icons/dynamic-field';
import { IconRestart } from '@components/icons/restart';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { LabelFieldTable } from '@components/label-field-table/label-field-table';
import { Spinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

import { useTestAIAgent } from 'api/ai-agents/hooks';
import { useWebSocket } from 'api/hooks';
import { MappableVariable, VariableValue } from 'api/types';
import { Property } from 'ui/authenticated/main/pages/ai-agents/shared/output-list';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';

const FILE_PREFIX = 'file_field-';

export const useAIAgentTestModal = () => useModal(AIAgentTestModal, { variant: 'LARGE' });

type AIAgentTestModalProps = {
  actionId: string;
  variables: VariableValue['variables'];
  outputSettings: Array<Property>;
  additionalFiles: Array<VariableValue>;
  onFileSelect(file: File | null): Promise<FileResponseObject>;
};

const AIAgentTestModal = ({
  actionId,
  variables = {},
  additionalFiles = [],
  outputSettings,
  onFileSelect,
}: AIAgentTestModalProps) => {
  const { t } = useScopedTranslate(locale);
  const testMutation = useTestAIAgent();
  const [isLoading, setIsLoading] = useState(false);
  const [output, setOutput] = useState<any>(undefined);

  const websocketId = useRef(uuid()).current;

  const onSubmit = (formValues: any) => {
    const withoutFiles = Object.fromEntries(
      Object.entries(formValues).filter(([key]) => !key.startsWith(FILE_PREFIX))
    );
    const files = additionalFiles.map((file) =>
      file.variables
        ? formValues[`${FILE_PREFIX}${file.value.replace('{{ ', '').replace(' }}', '')}`]
        : file.value
    );
    testMutation.mutateAsync({ actionId, websocketId, additionalFiles: files, ...withoutFiles });
    setIsLoading(true);
  };
  const fieldMappedFiles = additionalFiles.map((file) => file.variables).filter(Boolean) as Array<
    Record<string, MappableVariable>
  >;

  useWebSocket(`actions/ai-agents/${actionId}/${websocketId}/`, (data: MessageEvent<string>) => {
    setOutput(JSON.parse(data.data).content);
    setIsLoading(false);
  });

  if (output) {
    return (
      <Output
        output={output}
        outputSettings={outputSettings}
        onReset={() => setOutput(undefined)}
      />
    );
  }

  return (
    <Modal>
      {isLoading && <Spinner />}
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Form onSubmit={onSubmit}>
          <Stack space={6}>
            <Text text={t('instructions')} font="regular14" color="textSecondary" />
            <LabelFieldTable>
              <LabelFieldTable.HeaderRow>
                <LabelFieldTable.HeaderColumn label={t('field')} icon={IconDynamicField} />
                <LabelFieldTable.HeaderColumn label={t('testValue')} icon={IconSend} />
              </LabelFieldTable.HeaderRow>
              <tbody>
                {Object.entries(variables).map(([key, value]) => {
                  return (
                    <LabelFieldTable.Row
                      key={key}
                      id={key}
                      label={value.label}
                      icon={IconDynamicField}
                    >
                      <Form.TextField
                        isDisabled={isLoading}
                        id={key}
                        label={value.label}
                        hideLabel
                        validators={['required']}
                      />
                    </LabelFieldTable.Row>
                  );
                })}
                {fieldMappedFiles.map((file) => {
                  const [key, value] = Object.entries(file)[0];
                  return (
                    <LabelFieldTable.Row
                      key={key}
                      id={key}
                      label={value.label}
                      icon={IconDynamicField}
                    >
                      <Form.InstantUploadFileField
                        isDisabled={isLoading}
                        id={`${FILE_PREFIX}${key}`}
                        label={value.label}
                        hideLabel
                        onSelect={onFileSelect}
                        validators={['required']}
                      />
                    </LabelFieldTable.Row>
                  );
                })}
              </tbody>
            </LabelFieldTable>
          </Stack>
          <Form.Actions portal="test-action-footer">
            <Form.Reset text={t('core.cancel')} onClick={hideModal} />
            <Form.Submit text={t('test')} isDisabled={isLoading} />
          </Form.Actions>
        </Form>
      </Modal.Content>
      <Modal.Footer id="test-action-footer" />
    </Modal>
  );
};

const Output = ({
  output,
  outputSettings,
  onReset,
}: {
  output: { hasError?: boolean; message?: string; value?: Record<string, string> };
  outputSettings: AIAgentTestModalProps['outputSettings'];
  onReset: () => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const handleRestart = () => {
    onReset();
  };
  const hasError = output.hasError;
  const outputValue = output.value ?? {};
  const outputData = hasError ? (
    <Card spaceX={4} spaceY={2} interactive>
      <Stack orientation="horizontal" justify="space-between">
        <Stack orientation="horizontal" space={2} align="flex-start">
          <Text text={output.message} />
        </Stack>
      </Stack>
    </Card>
  ) : (
    outputSettings.map((property) => (
      <Card key={property.name} spaceX={4} spaceY={2} interactive>
        <Stack orientation="horizontal" justify="space-between">
          <Stack orientation="horizontal" space={2} align="center">
            <AttributeIcon type={property.type} />
            <div>
              <Text text={property.label} font="regular12" color="textSecondary" />
              <Text text={outputValue[property.name]} />
            </div>
          </Stack>
        </Stack>
      </Card>
    ))
  );
  return (
    <Modal>
      <Modal.SimpleHeader title={t('output')} />
      <Modal.Content>
        <Stack space={2}>{outputData}</Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="space-between">
          <Stack space={2} orientation="horizontal">
            <IconStatusIndicator color={hasError ? 'red200' : 'green200'} />
            <Text font="regular14" text={t(hasError ? 'error' : 'success')} />
          </Stack>
          <Stack space={2} orientation="horizontal">
            <Button
              onClick={handleRestart}
              iconLeft={<IconRestart />}
              variant="secondary"
              text={t('restart')}
            />
            <Button
              onClick={hideModal}
              iconLeft={<IconCollapse />}
              variant="secondary"
              text={t('core.close')}
            />
          </Stack>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Test action',
    instructions: 'Add values and data to see an example of the action output',
    test: 'Test',
    field: 'Field',
    testValue: 'Test value',
    output: 'Output',
    success: 'Success',
    error: 'Error',
    restart: 'Restart',
  },
};
